import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, Users, Building, FileText, Shield, CheckCircle, Settings } from "lucide-react";
import { VoiceReferral } from "./VoiceReferral";
import { membersAPI } from "@/services/api/members";

interface Referral {
  id: string | number;
  referralNumber?: string; // Unique 10-digit referral number
  // Member Information
  memberMedicaidId: string;
  memberName: string;
  phoneNumber?: string;
  email?: string;
  dateOfBirth: string;
  zipCode: string;
  preferredLanguage: string;

  // Address Information
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;

  // Health Plan Information
  healthPlan: string;
  planType: "Managed Care" | "Fee-for-Service";

  // Provider Information
  referringProvider: {
    name: string;
    npi: string;
    organization: string;
    phone: string;
  };

  // Clinical Context
  primaryDiagnosis: string;
  riskScore?: number;
  sdohNeeds: string[];
  housingSubcategories?: string[];
  transportationSubcategories?: string[];
  urgencyLevel: "Immediate" | "Within 24h" | "Within 1 Week" | "Routine";
  availability?: string;
  additionalNotes?: string;

  // Authorization & Compliance
  authorizationRequired: boolean;
  consentObtained: boolean;
  hipaaCompliant: boolean;

  // Legacy fields for backward compatibility
  timeline: string;
  status: "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "Authorized" | "In Progress" | "On Hold" | "Completed";
  createdAt: string;

  // Processing method
  useMatchingEngine: boolean;
}

interface CreateReferralFormProps {
  onSubmit: (referral: Referral) => void | boolean | Promise<boolean>;
  onCancel: () => void;
  onBack?: () => void;
  onComplete?: () => void;
  initialData?: Referral; // Optional initial data for editing
  isEditMode?: boolean; // Flag to indicate edit mode
}

// Mock database removed - now using real API from WeaveIn backend for member lookup

export function CreateReferralForm({ onSubmit, onCancel, onBack, onComplete, initialData, isEditMode = false }: CreateReferralFormProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'text' | 'voice'>('text');

  // Add validation state
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Member lookup state
  const [isLookingUpMember, setIsLookingUpMember] = useState(false);
  const [memberLookupMessage, setMemberLookupMessage] = useState<string>("");

  // Phone number formatting function
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Format as (xxx) xxx-xxxx
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  // Add CSS to ensure text selection works
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .selectable-input input {
        user-select: text !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        cursor: text !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const [formData, setFormData] = useState({
    // Member Information
    firstName: initialData?.memberName.split(' ')[0] || "",
    lastName: initialData?.memberName.split(' ').slice(1).join(' ') || "",
    phoneNumber: initialData?.phoneNumber || "",
    email: initialData?.email || "",
    dateOfBirth: initialData?.dateOfBirth || "",
    zipCode: initialData?.zipCode || "",
    preferredLanguage: initialData?.preferredLanguage || "",

    // Address Information
    streetAddress: initialData?.streetAddress || "",
    city: initialData?.city || "",
    state: initialData?.state || "",
    country: initialData?.country || "USA",

    // Health Plan Information
    memberMedicaidId: initialData?.memberMedicaidId || "",
    healthPlan: initialData?.healthPlan || "",
    planType: (initialData?.planType || "Managed Care") as "Managed Care" | "Fee-for-Service",

    // Provider Information
    referringProvider: {
      name: initialData?.referringProvider?.name || "",
      npi: initialData?.referringProvider?.npi || "",
      organization: initialData?.referringProvider?.organization || "",
      phone: initialData?.referringProvider?.phone || ""
    },

    // Clinical Context
    primaryDiagnosis: initialData?.primaryDiagnosis || "",
    riskScore: initialData?.riskScore?.toString() || "",
    sdohNeeds: initialData?.sdohNeeds || ([] as string[]),
    housingSubcategories: initialData?.housingSubcategories || ([] as string[]),
    transportationSubcategories: initialData?.transportationSubcategories || ([] as string[]),
    urgencyLevel: (initialData?.urgencyLevel || "Routine") as "Immediate" | "Within 24h" | "Within 1 Week" | "Routine",

    // Authorization & Compliance
    authorizationRequired: initialData?.authorizationRequired || false,
    consentObtained: initialData?.consentObtained || false,
    hipaaCompliant: initialData?.hipaaCompliant !== undefined ? initialData.hipaaCompliant : true,

    // Legacy fields
    timeline: initialData?.timeline || "",
    additionalNotes: initialData?.additionalNotes || "",
    availability: initialData?.availability || "",
    status: initialData?.status || "Pending" as const,

    // Matching Engine Selection
    useMatchingEngine: initialData?.useMatchingEngine ?? true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("🔍 Form Data:", formData);
    console.log("🔍 SDoH Needs:", formData.sdohNeeds);
    console.log("🔍 Housing Subcategories:", formData.housingSubcategories);
    console.log("🔍 Transportation Subcategories:", formData.transportationSubcategories);
    console.log("🔍 Authorization Required:", formData.authorizationRequired);
    console.log("🔍 Consent Obtained:", formData.consentObtained);
    
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate required fields
    const errors: {[key: string]: string} = {};
    
    if (!formData.consentObtained) {
      errors.consentObtained = "Member consent must be obtained before submitting the referral.";
    }
    
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required.";
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required.";
    }
    
    if (!formData.memberMedicaidId.trim()) {
      errors.memberMedicaidId = "Medicaid ID is required.";
    }
    
    if (!formData.primaryDiagnosis.trim()) {
      errors.primaryDiagnosis = "Primary diagnosis is required.";
    }
    
    // Check if at least one SDOH need is selected
    const totalNeeds = formData.sdohNeeds.length + formData.housingSubcategories.length + formData.transportationSubcategories.length;
    if (totalNeeds === 0) {
      errors.sdohNeeds = "Please select at least one social determinant of health need.";
    }
    
    // If there are validation errors, show them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      console.log("❌ Validation errors:", errors);
      return;
    }
    
    // Create a single referral with all selected needs
    // Generate unique 10-digit referral number
    const generateReferralNumber = () => {
      const timestamp = Date.now().toString();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const referralNumber = (timestamp + random).slice(-10);
      return referralNumber;
    };

    const newReferral: Referral = {
      id: `${Date.now()}`,
      referralNumber: generateReferralNumber(), // Add unique 10-digit number
      memberMedicaidId: formData.memberMedicaidId,
      memberName: `${formData.firstName} ${formData.lastName}`,
      phoneNumber: formData.phoneNumber, // Include phone
      email: formData.email, // Include email
      dateOfBirth: formData.dateOfBirth,
      zipCode: formData.zipCode,
      preferredLanguage: formData.preferredLanguage,
      streetAddress: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      healthPlan: formData.healthPlan,
      planType: formData.planType,
      referringProvider: formData.referringProvider,
      primaryDiagnosis: formData.primaryDiagnosis,
      riskScore: formData.riskScore ? parseFloat(formData.riskScore) : undefined,
      sdohNeeds: formData.sdohNeeds,
      housingSubcategories: formData.housingSubcategories,
      transportationSubcategories: formData.transportationSubcategories,
      urgencyLevel: formData.urgencyLevel,
      authorizationRequired: formData.authorizationRequired,
      consentObtained: formData.consentObtained,
      hipaaCompliant: true, // Always true for HIPAA compliance
      availability: formData.availability, // Include availability
      additionalNotes: formData.additionalNotes,
      timeline: formData.urgencyLevel,
      status: "Pending",
      createdAt: new Date().toISOString(),
      useMatchingEngine: formData.useMatchingEngine
    };

    // SECURITY: Removed PII logging - referral object contains firstName, lastName, email, phone, etc.
    console.log("📝 Submitting referral");

    try {
      const result = await onSubmit(newReferral);
      const success = result !== false; // Treat void and true as success

      if (success) {
        console.log("🎉 Referral created successfully!");

        // Call completion handler to close form and navigate
        if (onComplete) {
          onComplete();
        }
      } else {
        console.error("❌ Failed to create referral");
        setValidationErrors(prev => ({
          ...prev,
          submit: "Failed to create referral. Please try again."
        }));
        return;
      }
    } catch (error) {
      console.error("❌ Error creating referral:", error);
      setValidationErrors(prev => ({
        ...prev,
        submit: "An error occurred while creating the referral. Please try again."
      }));
      return;
    }
    
    // Reset form after creating all referrals
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      dateOfBirth: "",
      zipCode: "",
      preferredLanguage: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "USA",
      memberMedicaidId: "",
      healthPlan: "",
      planType: "Managed Care" as "Managed Care" | "Fee-for-Service",
      referringProvider: {
        name: "",
        npi: "",
        organization: "",
        phone: ""
      },
      primaryDiagnosis: "",
      riskScore: "",
      sdohNeeds: [] as string[],
      housingSubcategories: [] as string[],
      transportationSubcategories: [] as string[],
      urgencyLevel: "Routine" as "Immediate" | "Within 24h" | "Within 1 Week" | "Routine",
      authorizationRequired: false,
      consentObtained: false,
      hipaaCompliant: true,
      availability: "",
      timeline: "",
      additionalNotes: "",
      status: "Pending" as const,
      useMatchingEngine: true
    });
  };

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  // Autofill function for Medicaid ID lookup - using real API
  const handleMedicaidIdChange = async (medicaidId: string) => {
    setFormData(prev => ({ ...prev, memberMedicaidId: medicaidId }));

    // Clear previous lookup message
    setMemberLookupMessage("");

    // Trigger lookup when ID is at least 9 characters
    if (medicaidId.length >= 9) {
      setIsLookingUpMember(true);
      try {
        const result = await membersAPI.lookupMemberByMedicaidId(medicaidId);

        if (result.found && result.member) {
          // SECURITY: Removed member name from console log to prevent PII exposure
          console.log(`🔍 Found member in database`);
          setMemberLookupMessage("Member found! Auto-filling form with existing data.");

          const member = result.member;
          setFormData(prev => ({
            ...prev,
            // Member Information
            firstName: member.first_name || prev.firstName,
            lastName: member.last_name || prev.lastName,
            phoneNumber: member.phone_number || member.phone_no || prev.phoneNumber,
            email: member.email || prev.email,
            dateOfBirth: member.date_of_birth || member.dob || prev.dateOfBirth,
            zipCode: member.zip_code || prev.zipCode,
            preferredLanguage: member.preferred_language || member.language || prev.preferredLanguage,
            // Address Information
            streetAddress: member.street_address || prev.streetAddress,
            city: member.city || prev.city,
            state: member.state || prev.state,
            country: member.country || prev.country
          }));
        } else {
          console.log(`ℹ️ Member not found in database - creating new member profile`);
          setMemberLookupMessage("Member not found. Creating new member profile.");
        }
      } catch (error) {
        console.error("Error looking up member:", error);
        setMemberLookupMessage("Error looking up member. Please try again or continue with manual entry.");
      } finally {
        setIsLookingUpMember(false);
      }
    }
  };

  const handleProviderChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      referringProvider: {
        ...prev.referringProvider,
        [field]: value
      }
    }));
  };

  const handleSdohNeedsChange = (need: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      sdohNeeds: checked 
        ? [...prev.sdohNeeds, need]
        : prev.sdohNeeds.filter(n => n !== need),
      // Clear housing subcategories if housing assistance is unchecked
      housingSubcategories: need === "Housing Assistance" && !checked 
        ? [] 
        : prev.housingSubcategories,
      // Clear transportation subcategories if transportation is unchecked
      transportationSubcategories: need === "Transportation" && !checked 
        ? [] 
        : prev.transportationSubcategories
    }));
    
    // Clear SDOH validation error when any need is selected
    if (checked && validationErrors.sdohNeeds) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sdohNeeds;
        return newErrors;
      });
    }
  };

  const handleHousingSubcategoryChange = (subcategory: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      housingSubcategories: checked 
        ? [...prev.housingSubcategories, subcategory]
        : prev.housingSubcategories.filter(s => s !== subcategory)
    }));
    
    // Clear SDOH validation error when any subcategory is selected
    if (checked && validationErrors.sdohNeeds) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sdohNeeds;
        return newErrors;
      });
    }
  };

  const handleTransportationSubcategoryChange = (subcategory: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      transportationSubcategories: checked 
        ? [...prev.transportationSubcategories, subcategory]
        : prev.transportationSubcategories.filter(s => s !== subcategory)
    }));
    
    // Clear SDOH validation error when any subcategory is selected
    if (checked && validationErrors.sdohNeeds) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sdohNeeds;
        return newErrors;
      });
    }
  };

  const handleMatchingEngineChange = (useMatchingEngine: boolean) => {
    setFormData(prev => ({
      ...prev,
      useMatchingEngine
    }));
  };

  // Handle voice referral completion
  const handleVoiceReferralComplete = (voiceData: any) => {
    // SECURITY: Removed PII logging - voiceData contains firstName, lastName, phone, email, etc.
    console.log('📥 Received voice referral data');

    // Map voice data to form fields
    setFormData(prev => ({
      ...prev,
      firstName: voiceData.firstName || prev.firstName,
      lastName: voiceData.lastName || prev.lastName,
      phoneNumber: voiceData.phoneNumber || prev.phoneNumber,
      email: voiceData.email || prev.email,
      dateOfBirth: voiceData.dateOfBirth || prev.dateOfBirth,
      zipCode: voiceData.zipCode || prev.zipCode,
      preferredLanguage: voiceData.preferredLanguage || prev.preferredLanguage,
      streetAddress: voiceData.streetAddress || prev.streetAddress,
      city: voiceData.city || prev.city,
      state: voiceData.state || prev.state,
      country: voiceData.country || prev.country,
      memberMedicaidId: voiceData.memberMedicaidId || prev.memberMedicaidId,
      healthPlan: voiceData.healthPlan || prev.healthPlan,
      planType: voiceData.planType || prev.planType,
      referringProvider: {
        name: voiceData.referringProvider?.name || prev.referringProvider.name,
        npi: voiceData.referringProvider?.npi || prev.referringProvider.npi,
        organization: voiceData.referringProvider?.organization || prev.referringProvider.organization,
        phone: voiceData.referringProvider?.phone || prev.referringProvider.phone
      },
      primaryDiagnosis: voiceData.primaryDiagnosis || prev.primaryDiagnosis,
      riskScore: voiceData.riskScore || prev.riskScore,
      sdohNeeds: voiceData.sdohNeeds || prev.sdohNeeds,
      housingSubcategories: voiceData.housingSubcategories || prev.housingSubcategories,
      transportationSubcategories: voiceData.transportationSubcategories || prev.transportationSubcategories,
      urgencyLevel: voiceData.urgencyLevel || voiceData.timeline || prev.urgencyLevel,
      availability: voiceData.availability || prev.availability,
      additionalNotes: voiceData.additionalNotes || prev.additionalNotes,
      authorizationRequired: voiceData.authorizationRequired !== undefined ? voiceData.authorizationRequired : prev.authorizationRequired,
      consentObtained: voiceData.consentObtained !== undefined ? voiceData.consentObtained : prev.consentObtained,
      hipaaCompliant: voiceData.hipaaCompliant !== undefined ? voiceData.hipaaCompliant : prev.hipaaCompliant,
      useMatchingEngine: voiceData.useMatchingEngine !== undefined ? voiceData.useMatchingEngine : prev.useMatchingEngine,
      status: voiceData.status || prev.status
    }));

    console.log('✅ Form data updated from voice referral');

    // Switch to text tab to review the populated form
    setActiveTab('text');
  };

  return (
    <div className="bg-paper p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
       {/* Toolbar */}
       <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
         <div className="flex items-center space-x-3 md:space-x-4">
           {onBack && (
             <button
               onClick={onBack}
               className="p-2 hover:bg-surface rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px]"
             >
               <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-ink-500" />
             </button>
           )}
           {isEditMode && initialData && (
             <p className="text-[13px] text-ink-500">
               Updating referral for <span className="font-medium">{initialData.memberName}</span>
             </p>
           )}
         </div>
       </div>

      {/* Main content area */}
      <div className="p-4 md:p-6 lg:p-8 pt-8 md:pt-16 relative">

        <div className="max-w-5xl mx-auto relative z-10">
          {/* Tabs for Text Input vs Voice Referral */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'text' | 'voice')} className="mb-6 md:mb-8">
            <TabsList className="grid w-full grid-cols-2 bg-surface border border-v2border rounded-2xl p-1.5 md:p-2 shadow-sm">
              <TabsTrigger
                value="text"
                className="data-[state=active]:bg-v2accent data-[state=active]:text-white text-ink-500 rounded-xl py-2.5 md:py-3 text-sm md:text-base lg:text-lg font-semibold transition-all duration-300 min-h-[44px]"
              >
                Text Input
              </TabsTrigger>
              <TabsTrigger
                value="voice"
                className="data-[state=active]:bg-v2accent data-[state=active]:text-white text-ink-500 rounded-xl py-2.5 md:py-3 text-sm md:text-base lg:text-lg font-semibold transition-all duration-300 min-h-[44px]"
              >
                Voice Referral
              </TabsTrigger>
            </TabsList>

            {/* Text Input Tab */}
            <TabsContent value="text" className="mt-6 md:mt-8">
          <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8 lg:space-y-12">
          {/* Member Information */}
          <Card className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden">
             <CardHeader className="pb-6 md:pb-10 border-b border-v2border rounded-t-3xl ">
              <CardTitle className="flex items-center gap-3 md:gap-6 text-lg md:text-xl font-semibold text-ink">
                 <div className="p-3 md:p-4 bg-paper-2 rounded-2xl">
                   <Users className="w-5 h-5 md:w-6 md:h-6 text-ink-500" />
                 </div>
                <span className="text-ink font-bold">Member Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="firstName" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>First Name</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="lastName" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Last Name</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="phoneNumber" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Phone Number</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phoneNumber"
                    placeholder="(xxx) xxx-xxxx"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange("phoneNumber", formatPhoneNumber(e.target.value))}
                    className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                    style={{
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    maxLength={14}
                    required
                  />
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="email" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Email</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
              </div>
              
              {/* Address Information - Moved up to be with ZIP Code */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-1 h-6 bg-ink-300 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-ink">Address Information</h3>
                </div>
                
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="streetAddress" className="text-sm md:text-base font-semibold text-gray-900">
                    Street Address
                  </Label>
                  <Input
                    id="streetAddress"
                    placeholder="Enter street address"
                    value={formData.streetAddress}
                    onChange={(e) => handleInputChange("streetAddress", e.target.value)}
                    className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-3 selectable-input">
                    <Label htmlFor="city" className="text-sm md:text-base font-semibold text-gray-900">
                      City
                    </Label>
                    <Input
                      id="city"
                      placeholder="Enter city"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                      style={{ 
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text',
                        cursor: 'text'
                      }}
                    />
                  </div>
                  
                  <div className="space-y-3 selectable-input">
                    <Label htmlFor="state" className="text-sm md:text-base font-semibold text-gray-900">
                      State
                    </Label>
                    <Select value={formData.state} onValueChange={(value) => handleInputChange("state", value)}>
                      <SelectTrigger className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectItem value="Alabama">Alabama</SelectItem>
                        <SelectItem value="Alaska">Alaska</SelectItem>
                        <SelectItem value="Arizona">Arizona</SelectItem>
                        <SelectItem value="Arkansas">Arkansas</SelectItem>
                        <SelectItem value="California">California</SelectItem>
                        <SelectItem value="Colorado">Colorado</SelectItem>
                        <SelectItem value="Connecticut">Connecticut</SelectItem>
                        <SelectItem value="Delaware">Delaware</SelectItem>
                        <SelectItem value="Florida">Florida</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Hawaii">Hawaii</SelectItem>
                        <SelectItem value="Idaho">Idaho</SelectItem>
                        <SelectItem value="Illinois">Illinois</SelectItem>
                        <SelectItem value="Indiana">Indiana</SelectItem>
                        <SelectItem value="Iowa">Iowa</SelectItem>
                        <SelectItem value="Kansas">Kansas</SelectItem>
                        <SelectItem value="Kentucky">Kentucky</SelectItem>
                        <SelectItem value="Louisiana">Louisiana</SelectItem>
                        <SelectItem value="Maine">Maine</SelectItem>
                        <SelectItem value="Maryland">Maryland</SelectItem>
                        <SelectItem value="Massachusetts">Massachusetts</SelectItem>
                        <SelectItem value="Michigan">Michigan</SelectItem>
                        <SelectItem value="Minnesota">Minnesota</SelectItem>
                        <SelectItem value="Mississippi">Mississippi</SelectItem>
                        <SelectItem value="Missouri">Missouri</SelectItem>
                        <SelectItem value="Montana">Montana</SelectItem>
                        <SelectItem value="Nebraska">Nebraska</SelectItem>
                        <SelectItem value="Nevada">Nevada</SelectItem>
                        <SelectItem value="New Hampshire">New Hampshire</SelectItem>
                        <SelectItem value="New Jersey">New Jersey</SelectItem>
                        <SelectItem value="New Mexico">New Mexico</SelectItem>
                        <SelectItem value="New York">New York</SelectItem>
                        <SelectItem value="North Carolina">North Carolina</SelectItem>
                        <SelectItem value="North Dakota">North Dakota</SelectItem>
                        <SelectItem value="Ohio">Ohio</SelectItem>
                        <SelectItem value="Oklahoma">Oklahoma</SelectItem>
                        <SelectItem value="Oregon">Oregon</SelectItem>
                        <SelectItem value="Pennsylvania">Pennsylvania</SelectItem>
                        <SelectItem value="Rhode Island">Rhode Island</SelectItem>
                        <SelectItem value="South Carolina">South Carolina</SelectItem>
                        <SelectItem value="South Dakota">South Dakota</SelectItem>
                        <SelectItem value="Tennessee">Tennessee</SelectItem>
                        <SelectItem value="Texas">Texas</SelectItem>
                        <SelectItem value="Utah">Utah</SelectItem>
                        <SelectItem value="Vermont">Vermont</SelectItem>
                        <SelectItem value="Virginia">Virginia</SelectItem>
                        <SelectItem value="Washington">Washington</SelectItem>
                        <SelectItem value="West Virginia">West Virginia</SelectItem>
                        <SelectItem value="Wisconsin">Wisconsin</SelectItem>
                        <SelectItem value="Wyoming">Wyoming</SelectItem>
                        <SelectItem value="District of Columbia">District of Columbia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-3 selectable-input">
                    <Label htmlFor="zipCode" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                      <span>ZIP Code</span>
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="zipCode"
                      placeholder="Enter ZIP code"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange("zipCode", e.target.value)}
                      className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                      style={{ 
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text',
                        cursor: 'text'
                      }}
                      required
                    />
                  </div>
                  
                  <div className="space-y-3 selectable-input">
                    <Label htmlFor="country" className="text-sm md:text-base font-semibold text-gray-900">
                      Country
                    </Label>
                    <Select value={formData.country} onValueChange={(value) => handleInputChange("country", value)}>
                      <SelectTrigger className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USA">United States</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="Mexico">Mexico</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Date of Birth and Language - Moved below address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="dateOfBirth" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Date of Birth</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                    className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="preferredLanguage" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Preferred Language</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.preferredLanguage} onValueChange={(value) => handleInputChange("preferredLanguage", value)}>
                    <SelectTrigger className="border-2 !border-blue-300 focus:!border-blue-500 focus:ring-4 focus:ring-blue-100 !outline-none transition-all duration-300 h-11 md:h-12 text-base md:text-lg rounded-xl !select-text !cursor-text min-h-[44px]">
                      <SelectValue placeholder="Select preferred language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Spanish">Spanish</SelectItem>
                      <SelectItem value="Mandarin">Mandarin</SelectItem>
                      <SelectItem value="Cantonese">Cantonese</SelectItem>
                      <SelectItem value="Vietnamese">Vietnamese</SelectItem>
                      <SelectItem value="Korean">Korean</SelectItem>
                      <SelectItem value="Tagalog">Tagalog</SelectItem>
                      <SelectItem value="Arabic">Arabic</SelectItem>
                      <SelectItem value="French">French</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Decorative separator */}
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-0.5 bg-v2border"></div>
              <div className="w-3 h-3 bg-ink-300 rounded-full"></div>
              <div className="w-16 h-0.5 bg-v2border"></div>
            </div>
          </div>

          {/* Health Plan Information */}
          <Card className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
            <CardHeader className="pb-10 border-b border-v2border rounded-t-3xl ">
              <CardTitle className="flex items-center gap-6 text-xl font-semibold text-ink">
                <div className="p-4 bg-paper-2 rounded-2xl">
                  <Building className="w-6 h-6 text-ink-500" />
                </div>
                <span className="text-ink font-bold">Health Plan Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="memberMedicaidId" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Medicaid ID</span>
                    <span className="text-red-500">*</span>
                    {isEditMode && <span className="text-xs text-gray-500 font-normal">(Cannot be changed)</span>}
                  </Label>
                  <Input
                    id="memberMedicaidId"
                    placeholder="Enter Medicaid ID"
                    value={formData.memberMedicaidId}
                    onChange={(e) => handleMedicaidIdChange(e.target.value)}
                    disabled={isEditMode}
                    className={`border-2 ${isEditMode ? '!border-gray-300 !bg-gray-100 !cursor-not-allowed' : '!border-green-300 focus:!border-green-500 focus:ring-4 focus:ring-green-100'} !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text`}
                    style={{
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: isEditMode ? 'not-allowed' : 'text'
                    }}
                    required
                  />
                  {isLookingUpMember && (
                    <p className="text-sm text-blue-600 flex items-center space-x-2">
                      <span className="animate-spin">⏳</span>
                      <span>Looking up member...</span>
                    </p>
                  )}
                  {memberLookupMessage && !isLookingUpMember && (
                    <p className={`text-sm ${memberLookupMessage.includes('found') ? 'text-green-600' : memberLookupMessage.includes('Error') ? 'text-red-600' : 'text-gray-600'}`}>
                      {memberLookupMessage}
                    </p>
                  )}
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="healthPlan" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Health Plan</span>
                    <span className="text-xs font-normal text-gray-400">(optional)</span>
                  </Label>
                  <Select value={formData.healthPlan} onValueChange={(value) => handleInputChange("healthPlan", value)}>
                    <SelectTrigger className="border-2 !border-green-300 focus:!border-green-500 focus:ring-4 focus:ring-green-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text">
                      <SelectValue placeholder="Select health plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Anthem Blue Cross">Anthem Blue Cross</SelectItem>
                      <SelectItem value="Kaiser Permanente">Kaiser Permanente</SelectItem>
                      <SelectItem value="Health Net">Health Net</SelectItem>
                      <SelectItem value="Molina Healthcare">Molina Healthcare</SelectItem>
                      <SelectItem value="Centene Corporation">Centene Corporation</SelectItem>
                      <SelectItem value="UnitedHealthcare">UnitedHealthcare</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="planType" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Plan Type</span>
                    <span className="text-xs font-normal text-gray-400">(optional)</span>
                  </Label>
                  <Select value={formData.planType} onValueChange={(value) => handleInputChange("planType", value as "Managed Care" | "Fee-for-Service")}>
                    <SelectTrigger className="border-2 !border-green-300 focus:!border-green-500 focus:ring-4 focus:ring-green-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text">
                      <SelectValue placeholder="Select plan type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Managed Care">Managed Care</SelectItem>
                      <SelectItem value="Fee-for-Service">Fee-for-Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Decorative separator */}
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-0.5 bg-v2border"></div>
              <div className="w-3 h-3 bg-ink-300 rounded-full"></div>
              <div className="w-16 h-0.5 bg-v2border"></div>
            </div>
          </div>

          {/* Provider Information */}
          <Card className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
            <CardHeader className="pb-10 border-b border-v2border rounded-t-3xl ">
              <CardTitle className="flex items-center gap-6 text-xl font-semibold text-ink">
                <div className="p-4 bg-paper-2 rounded-2xl">
                  <FileText className="w-6 h-6 text-ink-500" />
                </div>
                <span className="text-ink font-bold">Referring Provider Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="providerName" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Provider Name</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="providerName"
                    placeholder="Enter provider's full name"
                    value={formData.referringProvider.name}
                    onChange={(e) => handleProviderChange("name", e.target.value)}
                    className="border-2 !border-purple-300 focus:!border-purple-500 focus:ring-4 focus:ring-purple-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="providerNPI" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>NPI Number</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="providerNPI"
                    placeholder="Enter 10-digit NPI"
                    value={formData.referringProvider.npi}
                    onChange={(e) => handleProviderChange("npi", e.target.value)}
                    className="border-2 !border-purple-300 focus:!border-purple-500 focus:ring-4 focus:ring-purple-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="providerOrganization" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Organization</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="providerOrganization"
                    placeholder="Enter organization name"
                    value={formData.referringProvider.organization}
                    onChange={(e) => handleProviderChange("organization", e.target.value)}
                    className="border-2 !border-purple-300 focus:!border-purple-500 focus:ring-4 focus:ring-purple-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="providerPhone" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Phone Number</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="providerPhone"
                    placeholder="(xxx) xxx-xxxx"
                    value={formData.referringProvider.phone}
                    onChange={(e) => handleProviderChange("phone", formatPhoneNumber(e.target.value))}
                    className="border-2 !border-purple-300 focus:!border-purple-500 focus:ring-4 focus:ring-purple-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text"
                    style={{
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    maxLength={14}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Decorative separator */}
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-0.5 bg-v2border"></div>
              <div className="w-3 h-3 bg-ink-300 rounded-full"></div>
              <div className="w-16 h-0.5 bg-v2border"></div>
            </div>
          </div>

          {/* Step 2: Needs and Timeline */}
          <Card className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
             <CardHeader className="pb-10 border-b border-v2border rounded-t-3xl ">
              <CardTitle className="flex items-center gap-6 text-xl font-semibold text-ink">
                 <div className="p-4 bg-paper-2 rounded-2xl">
                   <MapPin className="w-6 h-6 text-ink-500" />
                 </div>
                <span className="text-ink font-bold">Service Requirements</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
              {/* Clinical Context */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="primaryDiagnosis" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Primary Diagnosis</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="primaryDiagnosis"
                    placeholder="Enter primary diagnosis or condition"
                    value={formData.primaryDiagnosis}
                    onChange={(e) => handleInputChange("primaryDiagnosis", e.target.value)}
                    className="border-2 !border-indigo-300 focus:!border-indigo-500 focus:ring-4 focus:ring-indigo-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    required
                  />
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="riskScore" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Risk Score (Optional)</span>
                  </Label>
                  <Input
                    id="riskScore"
                    type="number"
                    placeholder="Enter risk score (0-100)"
                    value={formData.riskScore}
                    onChange={(e) => handleInputChange("riskScore", e.target.value)}
                    className="border-2 !border-indigo-300 focus:!border-indigo-500 focus:ring-4 focus:ring-indigo-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text"
                    style={{ 
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                      cursor: 'text'
                    }}
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              {/* SDOH Needs - Multiple Selection */}
              <div className="space-y-3">
                <Label className={`text-base font-semibold flex items-center space-x-2 ${
                  validationErrors.sdohNeeds ? 'text-red-800' : 'text-gray-900'
                }`}>
                  <span>Social Determinants of Health Needs</span>
                  <span className="text-red-500">*</span>
                </Label>
                {validationErrors.sdohNeeds && (
                  <p className="text-red-600 text-sm">{validationErrors.sdohNeeds}</p>
                )}
                {/* 2-Column Layout for SDOH Sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Housing Assistance Section */}
                  <div className="p-4 bg-paper-2 border border-v2border rounded-xl">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
                      <span className="text-sm md:text-base font-semibold text-ink">Housing Assistance</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        "Housing navigation",
                        "Housing deposits / move-in costs",
                        "Housing tenancy support",
                        "Transitional housing"
                      ].map((subcategory) => (
                        <label key={subcategory} className="flex items-start sm:items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 bg-surface border border-v2border rounded-lg hover:bg-paper transition-all duration-300 cursor-pointer group min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={formData.housingSubcategories.includes(subcategory)}
                            onChange={(e) => {
                              console.log('🏠 Housing checkbox clicked:', subcategory, e.target.checked);
                              handleHousingSubcategoryChange(subcategory, e.target.checked);
                            }}
                            className="w-5 h-5 flex-shrink-0 text-v2accent border-v2border rounded focus:ring-v2accent focus:ring-2 cursor-pointer"
                            style={{ pointerEvents: 'auto' }}
                          />
                          <span className="text-xs sm:text-sm font-medium text-ink leading-tight">{subcategory}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Transportation Assistance Section */}
                  <div className="p-4 bg-paper-2 border border-v2border rounded-xl">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
                      <span className="text-sm md:text-base font-semibold text-ink">Transportation Assistance</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        "Non-Emergency Medical Transportation (NEMT)",
                        "Bus passes / transit vouchers"
                      ].map((subcategory) => (
                        <label key={subcategory} className="flex items-start sm:items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 bg-surface border border-v2border rounded-lg hover:bg-paper transition-all duration-300 cursor-pointer group min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={formData.transportationSubcategories.includes(subcategory)}
                            onChange={(e) => {
                              console.log('🚗 Transportation checkbox clicked:', subcategory, e.target.checked);
                              handleTransportationSubcategoryChange(subcategory, e.target.checked);
                            }}
                            className="w-5 h-5 flex-shrink-0 text-v2accent border-v2border rounded focus:ring-v2accent focus:ring-2 cursor-pointer"
                            style={{ pointerEvents: 'auto' }}
                          />
                          <span className="text-xs sm:text-sm font-medium text-ink leading-tight">{subcategory}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Other Needs */}
                  <div className="p-4 bg-paper-2 border border-v2border rounded-xl md:col-span-2">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
                      <span className="text-sm md:text-base font-semibold text-ink">Other Needs</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        "Food Assistance", 
                        "Mental Health Support",
                        "Substance Abuse Treatment",
                        "Financial Assistance",
                        "Healthcare Access",
                        "Education Support",
                        "Employment Support",
                        "Legal Services",
                        "Childcare Support",
                        "Utility Assistance"
                      ].map((need) => (
                        <label key={need} className="flex items-start sm:items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 bg-surface border border-v2border rounded-xl hover:bg-paper transition-all duration-300 cursor-pointer group min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={formData.sdohNeeds.includes(need)}
                            onChange={(e) => {
                              console.log('📋 SDOH checkbox clicked:', need, e.target.checked);
                              handleSdohNeedsChange(need, e.target.checked);
                            }}
                            className="w-5 h-5 flex-shrink-0 text-v2accent border-v2border rounded focus:ring-v2accent focus:ring-2 cursor-pointer"
                            style={{ pointerEvents: 'auto' }}
                          />
                          <span className="text-xs sm:text-sm font-medium text-ink leading-tight">{need}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Label htmlFor="availability" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Availability</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.availability}
                    onValueChange={(value) => handleInputChange("availability", value)}
                  >
                    <SelectTrigger className="border-2 !border-indigo-300 focus:!border-indigo-500 focus:ring-4 focus:ring-indigo-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl">
                      <SelectValue placeholder="Select availability" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="Weekdays 9am-5pm">Weekdays 9am-5pm (based on local time)</SelectItem>
                      <SelectItem value="Weekends">Weekends</SelectItem>
                      <SelectItem value="Anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3 selectable-input">
                  <Label htmlFor="urgencyLevel" className="text-sm md:text-base font-semibold text-gray-900 flex items-center space-x-2">
                    <span>Urgency Level</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.urgencyLevel} onValueChange={(value) => handleInputChange("urgencyLevel", value as "Immediate" | "Within 24h" | "Within 1 Week" | "Routine")}>
                    <SelectTrigger className="border-2 !border-indigo-300 focus:!border-indigo-500 focus:ring-4 focus:ring-indigo-100 !outline-none transition-all duration-300 h-12 text-lg rounded-xl !select-text !cursor-text">
                      <SelectValue placeholder="Select urgency level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Immediate">Immediate (Emergency)</SelectItem>
                      <SelectItem value="Within 24h">Within 24 Hours</SelectItem>
                      <SelectItem value="Within 1 Week">Within 1 Week</SelectItem>
                      <SelectItem value="Routine">Routine (Standard)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="additionalNotes" className="text-sm md:text-base font-semibold text-gray-900">Additional Notes</Label>
                <Textarea
                  id="additionalNotes"
                  placeholder="Any additional information or special requirements..."
                  value={formData.additionalNotes}
                  onChange={(e) => handleInputChange("additionalNotes", e.target.value)}
                  className="border-2 !border-indigo-300 focus:!border-indigo-500 focus:ring-4 focus:ring-indigo-100 !outline-none transition-all duration-300 text-lg rounded-xl !select-text !cursor-text"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Decorative separator */}
          <div className="flex items-center justify-center py-6">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-0.5 bg-v2border"></div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
                <div className="w-3 h-3 bg-ink-300 rounded-full"></div>
                <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
              </div>
              <div className="w-20 h-0.5 bg-v2border"></div>
            </div>
          </div>

          {/* Compliance & Authorization */}
          <Card className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
            <CardHeader className="pb-10 border-b border-v2border rounded-t-3xl ">
              <CardTitle className="flex items-center gap-6 text-xl font-semibold text-ink">
                <div className="p-4 bg-paper-2 rounded-2xl">
                  <Shield className="w-6 h-6 text-ink-500" />
                </div>
                <span className="text-ink font-bold">Compliance & Authorization</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-4 bg-surface border border-v2border rounded-xl hover:bg-paper-2 transition-all duration-300 cursor-pointer group">
                    <input
                      type="checkbox"
                      id="authorizationRequired"
                      checked={formData.authorizationRequired}
                      onChange={(e) => {
                        console.log('🔐 Authorization checkbox clicked:', e.target.checked);
                        handleInputChange("authorizationRequired", e.target.checked);
                      }}
                      className="w-5 h-5 text-v2accent border-v2border rounded focus:ring-v2accent focus:ring-2 cursor-pointer"
                      style={{ pointerEvents: 'auto' }}
                    />
                    <label htmlFor="authorizationRequired" className="text-sm md:text-base font-semibold text-ink cursor-pointer">
                      Prior Authorization Required
                    </label>
                  </div>
                  
                  <div className={`flex items-center space-x-3 p-4 rounded-xl hover:bg-paper-2 transition-all duration-300 cursor-pointer group ${
                    validationErrors.consentObtained
                      ? 'bg-red-50/80 border border-red-200/50'
                      : 'bg-surface border border-v2border'
                  }`}>
                    <input
                      type="checkbox"
                      id="consentObtained"
                      checked={formData.consentObtained}
                      onChange={(e) => {
                        console.log('✅ Consent checkbox clicked:', e.target.checked);
                        handleInputChange("consentObtained", e.target.checked);
                        // Clear validation error when checkbox is checked
                        if (e.target.checked && validationErrors.consentObtained) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.consentObtained;
                            return newErrors;
                          });
                        }
                      }}
                      className={`w-5 h-5 rounded focus:ring-2 cursor-pointer ${
                        validationErrors.consentObtained
                          ? 'text-red-600 border-red-300 focus:ring-red-500'
                          : 'text-v2accent border-v2border focus:ring-v2accent'
                      }`}
                      style={{ pointerEvents: 'auto' }}
                    />
                    <label htmlFor="consentObtained" className={`text-base font-semibold cursor-pointer ${
                      validationErrors.consentObtained ? 'text-red-800' : 'text-ink'
                    }`}>
                      Member Consent Obtained <span className="text-red-500">*</span>
                    </label>
                  </div>
                  {validationErrors.consentObtained && (
                    <p className="text-red-600 text-sm mt-1 ml-2">{validationErrors.consentObtained}</p>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-4 bg-green-50/80 border border-green-200/50 rounded-xl !select-text !cursor-text">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm md:text-base font-semibold text-gray-900">HIPAA Compliant</span>
                  </div>
                  
                  <div className="p-4 bg-v2accent-soft border border-v2border rounded-xl !select-text !cursor-text">
                    <p className="text-sm text-v2accent-ink font-medium">
                      This referral system is designed to meet HIPAA compliance requirements and state Medicaid regulations.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Decorative separator */}
          <div className="flex items-center justify-center py-6">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-0.5 bg-v2border"></div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
                <div className="w-3 h-3 bg-ink-300 rounded-full"></div>
                <div className="w-2 h-2 bg-ink-300 rounded-full"></div>
              </div>
              <div className="w-20 h-0.5 bg-v2border"></div>
            </div>
          </div>

          {/* Matching Engine Selection */}
          <Card className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
            <CardHeader className="pb-6 border-b border-v2border rounded-t-3xl">
              <CardTitle className="flex items-center gap-6 text-xl font-semibold text-ink">
                <div className="p-3 bg-paper-2 rounded-2xl">
                  <Settings className="w-6 h-6 text-ink-500" />
                </div>
                <span className="text-ink font-bold">Referral Processing Method</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-4 bg-surface border border-v2border rounded-xl hover:bg-paper-2 transition-all duration-300 cursor-pointer">
                  <input
                    type="radio"
                    id="matching-engine"
                    name="processing-method"
                    checked={formData.useMatchingEngine}
                    onChange={() => handleMatchingEngineChange(true)}
                    className="w-4 h-4 text-v2accent border-v2border rounded focus:ring-v2accent"
                  />
                  <label htmlFor="matching-engine" className="flex-1 cursor-pointer">
                    <div className="font-semibold text-lg text-ink">Use Matching Engine</div>
                    <div className="text-sm text-ink-500">Automatically match referral to best social care provider</div>
                  </label>
                </div>
                
                <div className="flex items-center space-x-3 p-4 bg-surface border border-v2border rounded-xl hover:bg-paper-2 transition-all duration-300 cursor-pointer">
                  <input
                    type="radio"
                    id="manual-select"
                    name="processing-method"
                    checked={!formData.useMatchingEngine}
                    onChange={() => handleMatchingEngineChange(false)}
                    className="w-4 h-4 text-v2accent border-v2border rounded focus:ring-v2accent"
                  />
                  <label htmlFor="manual-select" className="flex-1 cursor-pointer">
                    <div className="font-semibold text-lg text-ink">Manually Select Provider</div>
                    <div className="text-sm text-ink-500">Choose specific social care organization for this referral</div>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 md:gap-6 pt-6 md:pt-8">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
               className="border border-v2border bg-surface text-ink hover:bg-paper-2 transition-all duration-300 rounded-xl px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold min-h-[44px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
               className="bg-v2accent hover:bg-v2accent/90 text-white border-0 transition-all duration-300 rounded-2xl px-6 sm:px-8 md:px-12 py-3 md:py-4 text-base sm:text-lg md:text-xl font-bold whitespace-nowrap min-h-[44px]"
            >
              {isEditMode ? "Update Referral" : "Submit Referral"}
            </Button>
          </div>
        </form>
            </TabsContent>

            {/* Voice Referral Tab */}
            <TabsContent value="voice" className="mt-8">
              <VoiceReferral
                onComplete={handleVoiceReferralComplete}
                onCancel={() => setActiveTab('text')}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      </div>
    </div>
  );
}

export default CreateReferralForm;
